import { useEffect, useRef, useState } from "react";
import { FaFilePdf } from "react-icons/fa6";
import { Document, Page } from "react-pdf";
import { pdfFileNameFrom, usePdfBlobUrl } from "./PdfViewerModal";

/**
 * Page-1 thumbnail for a PDF, sized to sit alongside the image/video previews
 * used in reply strips. Deliberately not a link — the surrounding UI owns the
 * click (scrolling to a message, or cancelling a reply).
 */
const PdfThumbnail = ({ url, name, size = 64, labelClassName = "text-xs text-gray-600" }) => {
  const [inView, setInView] = useState(false);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef(null);
  const { blobUrl, error } = usePdfBlobUrl(inView ? url : null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const canRender = !!blobUrl && !failed && !error;
  const label = name ?? pdfFileNameFrom(url);

  return (
    <div ref={boxRef} className="flex items-center gap-2">
      <div
        className={`flex justify-center overflow-hidden bg-white border border-gray-200 rounded shrink-0 ${
          canRender ? "items-start" : "items-center"
        }`}
        style={{ width: size, height: size }}
      >
        {canRender ? (
          <Document
            file={blobUrl}
            onLoadError={() => setFailed(true)}
            loading={<FaFilePdf className="text-red-300" size={Math.round(size / 3)} />}
            error={null}
          >
            <Page
              pageNumber={1}
              width={size}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={null}
            />
          </Document>
        ) : (
          <FaFilePdf className="text-red-500" size={Math.round(size / 3)} />
        )}
      </div>
      <span className={`min-w-0 truncate ${labelClassName}`}>{label}</span>
    </div>
  );
};

export default PdfThumbnail;
